import { BlockStack, Button, Grid, Icon, InlineStack, Link, Text, Tooltip, useBreakpoints } from '@shopify/polaris';
import HoverMarquee from 'components/HoverMarquee';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import helpers from 'helpers/index';
import { TypedTripManifest } from 'queries/trip_manifest.query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { useCurrentTicketMoving } from 'context/CurrentTicketMovingContext';
import { CashDollarIcon, TaxIcon, BillIcon, CreditCardIcon, MoneyNoneIcon, AlertDiamondIcon } from '@shopify/polaris-icons';
import __ from 'languages/index';
import { useNavigate } from 'react-router-dom';
import { TypedTrip_manifest_ticket, useMutateMoving_ticket_to_trip } from 'queries/trip_manifest_ticket.query';
import { ENUM_TICKET_TYPE, useMutateTicketDelete, useMutateTicketDetach, useMutateTicketMerge } from 'queries/ticket.query';
import dateandtime from 'date-and-time';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';

const divAddNewTicket = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
};

function useHash() {
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return hash;
}

export default function TicketInManifest({
  ticketData,
  manifest,
  type = 'seat', // 'seat' cho vé, 'shipping' cho hàng hóa
  editButtonCallback,
  editTaxButtonCallback,
  payButtonCallback,
  onNewTicket,
}: {
  ticketData: TypedTrip_manifest_ticket[];
  manifest: TypedTripManifest;
  type?: string;
  editButtonCallback: (order_code: string) => void;
  editTaxButtonCallback: (order_code: string) => void;
  payButtonCallback: (order_code: string) => void;
  onNewTicket: (trip_manifest_id: any, type: string, position?: string[]) => void;
}) {
  const seatMap: string[] = String(manifest?.seating?.seating_available || ' ').split('|');
  const seatTotalChar = manifest?.seating?.seating_number;
  const seatAvailableCount = manifest?.seating?.seating_available_count;

  /** extract rootSearch path ... */
  const rootSearchPath = window.location.search;
  let StringQuery: Record<string, any> = useMemo(() => {
    return helpers.ExtractUrl(rootSearchPath);
  }, [rootSearchPath]);

  const { addNotification } = useNotification();
  const { mutateAsync: movingTicketCallback, isPending: loadingMovingTicket } = useMutateMoving_ticket_to_trip();

  /** Vị trí ghế đã ngồi */
  /* Tự động highlight ghế nếu có hash trên URL */
  const hash = useHash();
  const highlight_many_orders = useMemo(() => {
    return String(hash || ' ')
      .replace('#', '')
      .trim()
      .split(',');
  }, [hash]);

  const [currentSeatActive, setCurrentSeatActive] = useState<Array<string | number>>([]);
  const [currentEmptyTicketToActive, setCurrentEmptyTicketToActive] = useState([]);

  const getTicketIdsByOrder = useCallback(
    (order_id: any) => {
      return ticketData.filter((ticket) => ticket.ticket?.order_id === order_id).map((ticket) => ticket.ticket_id);
    },
    [ticketData]
  );

  const currentSeatActiveCallback = useCallback(
    (order_id: any[]) => {
      setCurrentEmptyTicketToActive([]);
      let AL = [];
      for (let o of order_id) {
        AL = [...AL, ...getTicketIdsByOrder(o)];
      }
      return setCurrentSeatActive(AL);
    },
    [getTicketIdsByOrder]
  );

  const handleTicketClick = useCallback(
    (event, ticketId: string, orderId: string) => {
      const isMultiSelect = event.ctrlKey || event.metaKey;
      setCurrentEmptyTicketToActive([]);

      const orderTicketIds = getTicketIdsByOrder(orderId);

      setCurrentSeatActive((prev) => {
        if (isMultiSelect) {
          const next = new Set(prev);
          const allSelected = orderTicketIds.every((id) => next.has(id));

          if (allSelected) {
            orderTicketIds.forEach((id) => next.delete(id));
          } else {
            orderTicketIds.forEach((id) => next.add(id));
          }

          return Array.from(next);
        }

        return orderTicketIds.length ? orderTicketIds : [ticketId];
      });
    },
    [getTicketIdsByOrder]
  );

  useEffect(() => {
    if (!highlight_many_orders) return;
    currentSeatActiveCallback(highlight_many_orders);
  }, [highlight_many_orders, ticketData]);

  const searchAllTicketIDInThatOrder = useCallback(
    (order_id: string) => {
      // tìm tất thảy các ID của cái đơn hàng này
      const allTicketIDsInThatOrder = getTicketIdsByOrder(order_id);
      setCurrentSeatActive(allTicketIDsInThatOrder);
      // empty cac thang khac
      setCurrentEmptyTicketToActive([]);
      return allTicketIDsInThatOrder;
    },
    [getTicketIdsByOrder]
  );

  /** Chọn ghế trống */
  const onEmptyTicketClickToggleSelected = (indexNum) => {
    // Kiểm tra xem item đã được chọn chưa
    if (currentEmptyTicketToActive.includes(indexNum)) {
      // Nếu đã chọn, loại bỏ khỏi mảng
      setCurrentEmptyTicketToActive(currentEmptyTicketToActive.filter((itemId) => itemId !== indexNum));
    } else {
      // Nếu chưa được chọn, thêm vào mảng
      setCurrentEmptyTicketToActive([...currentEmptyTicketToActive, indexNum]);
    }
    setCurrentSeatActive([]);
  };

  /** Nút book luôn phải ăn chắc là đã include vị trí vào ồi */
  const makeSureClickMustIncludeForBookingButton = (indexNum: string) => {
    // Nếu chưa được chọn, thêm vào mảng
    let clonecurrentEmptyTicketToActive = [...currentEmptyTicketToActive];
    if (!currentEmptyTicketToActive.includes(indexNum)) {
      clonecurrentEmptyTicketToActive = [...currentEmptyTicketToActive, indexNum];
      setCurrentEmptyTicketToActive(clonecurrentEmptyTicketToActive);
    }
    setCurrentSeatActive([]);
    return clonecurrentEmptyTicketToActive;
  };

  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  const { currentTicketMoving, setCurrentTicketMoving, clearCurrentTicketMoving } = useCurrentTicketMoving();

  /** Cancel ticket */
  const { showDeleteConfirm } = useDeleteConfirm();
  const { mutateAsync: cancelATicket } = useMutateTicketDelete();
  const handleCancelTicket = useCallback(
    async (ticket_id) => {
      let ticketElement = ticketData?.find((t) => t.ticket_id === ticket_id);
      const isUserRequireTax = !!ticketElement?.ticket?.orders?.order_invoice_info;
      let confirmCancelTicket = await showDeleteConfirm(
        isUserRequireTax ? 'Dừng lại một giây!!!!!!!' : 'Bạn có chắc chắn muốn xóa vé này?',
        isUserRequireTax
          ? `Vé này đã được hành khách YÊU CẦU XUẤT HÓA ĐƠN, bạn phải biết bạn đang làm gì! Đây là việc không thể khôi phục.`
          : `Việc hủy vé có thể được thông báo cho khách hàng. Tài xế cũng có thể nhận được các thông báo tương tự.`
      );
      if (confirmCancelTicket) {
        try {
          await cancelATicket(ticket_id);
          setCurrentSeatActive([]);
        } catch (e) {}
      }
    },
    [ticketData]
  );

  /** Gộp vé */
  const { mutateAsync: mergeTicketsCallback } = useMutateTicketMerge();
  const handleMergeToOneOrderCallback = useCallback(
    async (order_id: any) => {
      //
      try {
        await mergeTicketsCallback({
          order_id,
          ticket_id: currentSeatActive,
        });
        setCurrentSeatActive([]);
        addNotification('info', 'Gộp vé thành công!');
      } catch (e) {}
    },
    [currentSeatActive]
  );

  /** tách vé */
  const { mutateAsync: detachATicket } = useMutateTicketDetach();
  const handleDetactTicket = useCallback(async (ticket_id) => {
    try {
      await detachATicket(ticket_id);
      addNotification('info', 'Tách vé thành công!');
      setCurrentSeatActive([ticket_id]);
    } catch (e) {}
  }, []);

  /** Bấm nút di chuyển ... */
  const [seatLoading, setSeatLoading] = useState<string | null>(null);
  const moveTicketToHere = useCallback(
    async (position_seatCode: string) => {
      // if (StringQuery.htext === '') {
      //   return addNotification('error', 'Vui lòng chọn giờ chạy trước!');
      // }

      setSeatLoading(position_seatCode);
      await helpers.sleep(500); /** Không delay không được, nó load nhanh quá không nhìn thấy ... */

      try {
        let ticketToMove: string | number | undefined;

        // Lấy vé đầu tiên và bỏ nó ra khỏi context
        if (!currentTicketMoving || currentTicketMoving.length === 0) {
          addNotification('info', 'Không có vé nào để di chuyển hoặc đã bị hủy.');
          setSeatLoading(null);
          clearCurrentTicketMoving();
          return;
        }

        const newTickets = [...currentTicketMoving];
        ticketToMove = newTickets.shift();
        setCurrentTicketMoving(newTickets);

        if (!ticketToMove) {
          setSeatLoading(null);
          clearCurrentTicketMoving();
          return;
        }

        /** Tạm thời disabled */
        await movingTicketCallback({
          seat_position: position_seatCode,
          trip_manifest_id: manifest?.id,
          ticket_id: BigInt(ticketToMove),
          /** Không cần nữa ... */
          // departure_date: new Date(`${StringQuery.d} ${StringQuery.htext}`).getTime(),
        });

        if (newTickets.length === 0) {
          clearCurrentTicketMoving();
        }
      } catch (e) {
        console.error(e, 'MOVING_TICKET_ERROR');
      } finally {
        setSeatLoading(null);
      }
    },
    [StringQuery, ticketData, manifest, currentTicketMoving]
  );

  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */
  /** kết thúc /// Chọn vé để di chuyển */

  /** Phát hiện click ra ngoài */
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        // 👉 Click ngoài
        setCurrentSeatActive([]);
        setCurrentEmptyTicketToActive([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigate = useNavigate();

  const { mdDown } = useBreakpoints();

  const _renderTicket = (item: TypedTrip_manifest_ticket, index, highlight = false) => {
    const isSelected = currentSeatActive?.some((id) => id === item?.ticket_id);
    const isMoving = helpers.isArray(currentTicketMoving) && currentTicketMoving?.some((id) => id === item?.ticket_id);
    return (
      <div
        className={`
          ticket_element ${item?.ticket?.orders?.transactions ? ' ticket_paid' : ''}
         order_id_${item.ticket?.order_id}
         ticket_index_${index}
          ${highlight ? ' actived' : ''}
          ${isSelected ? ' actived' : ''}
          ${isMoving ? ' ticket_highlight' : ''}
          `}
        key={index + '______ticketElement'}
        onClick={(e) => {
          handleTicketClick(e, item?.ticket_id, item?.ticket?.order_id);
        }}
      >
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Tooltip content={`Ticket id ${item?.ticket_id} - Order Code ${item?.ticket?.orders?.order_code}`}>
              <Text as="h3" variant="headingLg">
                {item.ticket?.orders.pickup_time}
              </Text>
            </Tooltip>
            <div className="ticket_badge_header_top">
              <InlineStack gap="200" align="end" blockAlign="center">
                {item?.ticket?.orders?.order_invoice_info ? (
                  <Button tone="success" variant="tertiary">
                    {item?.ticket?.orders?.order_invoice_info?.tax_type}
                  </Button>
                ) : null}

                {item?.ticket?.orders?.order_invoice_info ? (
                  <Tooltip content={item?.ticket?.orders?.order_invoice_info?.tax_type}>
                    <Icon source={TaxIcon} tone={item?.ticket?.orders?.order_invoice_info?.is_customer_required === 1 ? 'magic' : 'emphasis'} />
                  </Tooltip>
                ) : (
                  <Tooltip content="Thuế mặc định">
                    <Icon source={TaxIcon} tone="subdued" />
                  </Tooltip>
                )}
                {item?.ticket?.orders?.is_exported_invoice === 1 ? (
                  <Tooltip
                    content={`Đã xuất hóa đơn, lúc ${dateandtime.format(new Date(Number(item?.ticket?.orders?.invoiceExportedAt)), 'HH:mm DD-MM-YYYY')}`}
                  >
                    <Icon source={BillIcon} tone="emphasis" />
                  </Tooltip>
                ) : (
                  <Tooltip content="Chưa xuất hóa đơn">
                    <Icon source={BillIcon} tone="subdued" />
                  </Tooltip>
                )}

                {item?.ticket?.orders?.transactions ? (
                  <Tooltip
                    content={`Đã thanh toán ${item?.ticket?.orders?.transactions?.payment_method}, thu bởi ${item?.ticket?.orders?.transactions?.casher_data?.display_name} lúc ${dateandtime.format(new Date(Number(item?.ticket?.orders?.transactions?.createdAt)), 'HH:mm DD/MM/YYYY')}`}
                  >
                    {item?.ticket?.orders?.transactions?.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.CASH ? (
                      <Icon source={CashDollarIcon} tone="emphasis" />
                    ) : item?.ticket?.orders?.transactions?.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.BANK ? (
                      <Icon source={CreditCardIcon} tone="emphasis" />
                    ) : (
                      <Icon source={AlertDiamondIcon} tone="critical" />
                    )}
                  </Tooltip>
                ) : (
                  <Tooltip content="Chưa thanh toán">
                    <Icon source={MoneyNoneIcon} tone="subdued" />
                  </Tooltip>
                )}
              </InlineStack>
            </div>
          </InlineStack>
          <BlockStack gap="050" inlineAlign="end">
            <div className="ticket_address">
              <HoverMarquee speed={80}>{item?.ticket.orders?.pickup_address}</HoverMarquee>
            </div>
            <div className="ticket_address">
              <HoverMarquee speed={80}>{item?.ticket.orders?.dropoff_address}</HoverMarquee>
            </div>
          </BlockStack>
          <InlineStack align="space-between" blockAlign="start" gap="100">
            <BlockStack gap="050">
              <Text as="p" fontWeight="bold">
                <Link monochrome removeUnderline onClick={() => navigate('/customer/view/' + item.ticket.orders?.customer_data?.user_id)}>
                  {item.ticket.orders?.customer_data?.display_name} <br /> {item.ticket.orders?.customer_data?.user_phonenumber}
                </Link>
              </Text>
            </BlockStack>
            <div>
              <Text as="p" fontWeight="bold" alignment="end">
                Vé {__helpers.formatNumberWithCommas(item?.ticket.price - item?.ticket.discount)} /{' '}
                {__helpers.formatNumberWithCommas(item?.ticket?.orders?.total_must_pay || 0)}
              </Text>
            </div>
          </InlineStack>
          {item?.ticket.orders?.note ? (
            <Tooltip content={item?.ticket.orders?.note || 'Không có ghi chú!'}>
              <Text as="p" variant="bodySm" tone="subdued">
                Ghi chú: {item?.ticket.orders?.note}
              </Text>
            </Tooltip>
          ) : (
            <Text as="p" variant="bodySm" tone="subdued">
              Ghi chú: --
            </Text>
          )}
        </BlockStack>
        <br />

        <br />
        <br />
        <br />
        {item?.ticket?.orders?.is_exported_invoice !== 1 ? (
          <div className="toolchain">
            <button
              onClick={(e) => {
                e.stopPropagation();
                editButtonCallback(item?.ticket?.order_id);
              }}
              title="Cập nhật lại vé"
            >
              U
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                editTaxButtonCallback(item?.ticket?.order_id);
              }}
              title="Cập nhật thông tin thuế"
            >
              T
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                let allTicketSelected = searchAllTicketIDInThatOrder(item.ticket?.order_id);
                setCurrentTicketMoving(allTicketSelected);
              }}
              title="Di chuyển vé"
            >
              M
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCancelTicket(item.ticket_id);
              }}
              title="Hủy vé"
            >
              D
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                payButtonCallback(item.ticket?.order_id);
              }}
              title="Thanh toán cho đơn này"
            >
              P
            </button>

            {item.ticket.orders.total_seat > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDetactTicket(item.ticket_id);
                }}
                title="Tách vé"
              >
                S
              </button>
            )}
            {currentSeatActive.length > 1 && currentSeatActive.includes(item.ticket_id) ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMergeToOneOrderCallback(item?.ticket?.order_id);
                }}
                title="Gộp vé"
              >
                G
              </button>
            ) : null}
          </div>
        ) : (
          <div className="toolchain">
            <button
              onClick={(e) => {
                e.stopPropagation();
                editTaxButtonCallback(item?.ticket?.order_id);
              }}
              title="Cập nhật thông tin thuế"
            >
              T
            </button>
          </div>
        )}

        <div className="ticket_author">
          <Text as="p" alignment="end">
            {item.ticket?.orders?.author_data?.display_name}
          </Text>
        </div>
      </div>
    );
  };

  const is_exported_invoice = manifest ? manifest.is_exported_invoice === 1 : false;

  const sortTicketsForSeatStack = useCallback(
    (ticketsInSeat: TypedTrip_manifest_ticket[]) => {
      const scored = [...ticketsInSeat].sort((a, b) => {
        const aSelected = currentSeatActive?.includes(a.ticket_id) ? 1 : 0;
        const bSelected = currentSeatActive?.includes(b.ticket_id) ? 1 : 0;

        if (aSelected !== bSelected) return bSelected - aSelected;

        const aMoving = helpers.isArray(currentTicketMoving) && currentTicketMoving?.includes(a.ticket_id) ? 1 : 0;
        const bMoving = helpers.isArray(currentTicketMoving) && currentTicketMoving?.includes(b.ticket_id) ? 1 : 0;

        if (aMoving !== bMoving) return bMoving - aMoving;

        return Number(b.ticket_id) - Number(a.ticket_id);
      });

      return scored;
    },
    [currentSeatActive, currentTicketMoving]
  );

  return (
    <div ref={wrapperRef}>
      {type === 'seat' && (
        <Grid>
          {Array.from({ length: seatTotalChar }, (_, i) => i + 1).map((index) => {
            const position = seatMap[index - 1] || 'no_where';
            const ticketsInSeat = Array.isArray(ticketData) ? ticketData.filter((al) => al.seat_position === position) : [];
            if (index > seatAvailableCount)
              return (
                <Grid.Cell key={index} columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                  <div className="ticket_element disabled">Ghế không được phép đặt</div>
                </Grid.Cell>
              );
            const isMoving = helpers.isArray(currentTicketMoving) && currentTicketMoving.length > 0;

            return (
              <Droppable
                droppableId={`add_passenger_ticket:position_${position}:manifest_item_${manifest?.id}`}
                key={position + '_add_passenger_ticket'}
                type="SUB_ITEM"
                isDropDisabled={ticketsInSeat.length > 0 || !!mdDown || is_exported_invoice} // <- cấm drop nếu đã có ticket
              >
                {(provided, snapshot) => {
                  const sortedTicketsInSeat = sortTicketsForSeatStack(ticketsInSeat);
                  const topTicket = sortedTicketsInSeat[0];

                  return (
                    <Grid.Cell key={index} columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {topTicket ? (
                          <div className={`ticket_stack ${sortedTicketsInSeat.length > 1 ? ' has_multiple_ticket' : ''}`}>
                            {sortedTicketsInSeat.length > 1 && (
                              <div className="ticket_stack_decorators" aria-hidden="true">
                                {sortedTicketsInSeat.slice(1, 3).map((ticket, overlayIndex) => (
                                  <div className={`ticket_stack_decorator layer_${overlayIndex + 1}`} key={`trip_manifest_id_overlay_${ticket.id}`} />
                                ))}
                              </div>
                            )}

                            <Draggable
                              key={`trip_manifest_id_${topTicket.id}`}
                              draggableId={`trip_manifest_id_${topTicket.id}`}
                              index={index}
                              isDragDisabled={!!mdDown || isMoving || is_exported_invoice}
                            >
                              {(provided) => (
                                <div className="ticket_stack_item" ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                  {_renderTicket(topTicket, position, snapshot.isDraggingOver)}
                                </div>
                              )}
                            </Draggable>
                          </div>
                        ) : (
                          <div
                            className={`ticket_element clickable ${currentEmptyTicketToActive?.includes(position) ? ' actived' : ''} 
                              ${snapshot.isDraggingOver ? ' actived' : ''}
                              ${!helpers.isEmpty(currentTicketMoving) ? (currentTicketMoving.length <= manifest.seating.seating_available_count - manifest.total_occupied_seats ? ' ticket_moving_selectable' : 'ticket_moving_unselectable') : ''}
                              ${seatLoading !== null && seatLoading === position ? ' loading' : ''}
                              `}
                            onClick={() => {
                              if (
                                !helpers.isEmpty(currentTicketMoving) &&
                                currentTicketMoving.length <= manifest.seating.seating_available_count - manifest.total_occupied_seats
                              ) {
                                moveTicketToHere(position);
                              } else {
                                onEmptyTicketClickToggleSelected(position);
                              }
                            }}
                          >
                            <div style={divAddNewTicket}>
                              Ghế trống
                              {!is_exported_invoice ? (
                                <div className="toolchain">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const currentHoldPosition = makeSureClickMustIncludeForBookingButton(position); // bắt buộc ...
                                      onNewTicket(manifest.id, ENUM_TICKET_TYPE.PASSENGER, currentHoldPosition);
                                      setCurrentEmptyTicketToActive([]);
                                    }}
                                  >
                                    B
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {/* {provided.placeholder} */}
                      </div>
                    </Grid.Cell>
                  );
                }}
              </Droppable>
            );
          })}
        </Grid>
      )}

      {type === 'shipping' && (
        <Droppable
          droppableId={`add_shipping_ticket:position_trunk:manifest_item_${manifest?.id}`}
          key={manifest.id + 'add_shipping_ticket'}
          type="SUB_ITEM"
          isDropDisabled={!!mdDown}
        >
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <Grid>
                {Array.isArray(ticketData)
                  ? ticketData?.map((tmt, index) => {
                      return (
                        <Draggable
                          key={`trip_manifest_id_` + tmt.id}
                          draggableId={`trip_manifest_id_` + tmt.id}
                          index={index}
                          isDragDisabled={!!mdDown || is_exported_invoice}
                        >
                          {(provided) => (
                            <Grid.Cell key={`grid_` + tmt.id} columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                              <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                {_renderTicket(tmt, index + 111)}
                              </div>
                            </Grid.Cell>
                          )}
                        </Draggable>
                      );
                    })
                  : null}
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                  <div
                    className={`ticket_element ${snapshot.isDraggingOver ? 'actived' : ''}
                      ${snapshot.isDraggingOver ? ' actived' : ''}
                      ${!helpers.isEmpty(currentTicketMoving) ? ' ticket_moving_selectable' : ''}
                      ${seatLoading !== null && seatLoading === 'trunk' ? ' loading' : ''}
                    `}
                    onClick={() => {
                      /** Không kích hoạt nếu đã xuất vé */
                      if (is_exported_invoice) return;
                      if (!helpers.isEmpty(currentTicketMoving)) {
                        moveTicketToHere('trunk');
                      } else {
                        onNewTicket(manifest.id, ENUM_TICKET_TYPE.SHIPMENT, ['trunk']);
                      }
                    }}
                  >
                    <div style={divAddNewTicket}>Thêm hàng</div>
                  </div>
                  {/* {provided.placeholder} */}
                </Grid.Cell>
              </Grid>
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
}

